import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Eye, Sparkles, DoorOpen } from "lucide-react";
import { useApp } from "@/context/AppContext";

/** Il messaggio gentile quando in vetrina si tocca qualcosa che modificherebbe i dati. */
export const VetrinaBlocco = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const apri = () => setOpen(true);
    window.addEventListener("rh:solo-lettura", apri);
    return () => window.removeEventListener("rh:solo-lettura", apri);
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="vetrina-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-amber-500" /> Qui si guarda, non si tocca
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">
          Stai navigando in una vetrina: puoi entrare in ogni schermata e vedere come lavora un
          locale vero, ma i dati non si modificano — così resta pulita per chi arriva dopo di te.
        </p>
        <p className="text-sm text-slate-600">
          Per prendere una comanda, mandarla in cucina e sentire la voce che la legge, apri la tua
          demo: è gratis e la configuriamo sulla tua attività.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button className="h-12 flex-1" data-testid="vetrina-cta-btn" onClick={() => { window.location.href = "/prova"; }}>
            <Sparkles className="mr-1.5 h-4 w-4" /> Apri la tua demo in 60 secondi
          </Button>
          <Button variant="outline" className="h-12" onClick={() => setOpen(false)} data-testid="vetrina-close-btn">
            Continua a guardare
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** Nastro sempre visibile: chi guarda sa dove si trova, come provare davvero e come uscire. */
export const VetrinaNastro = () => {
  const { exitTenant } = useApp();
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 bg-amber-500 px-4 py-2 text-slate-950" data-testid="vetrina-nastro">
      <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide sm:text-sm">
        <Eye className="h-4 w-4" /> Prova a navigare nella demo
        <span className="hidden font-medium normal-case tracking-normal opacity-80 sm:inline">
          — guarda tutto quello che vuoi, i dati restano come sono
        </span>
      </p>
      <div className="flex items-center gap-2">
        <a href="/prova" data-testid="vetrina-nastro-cta">
          <Button size="sm" className="h-8 bg-slate-950 text-amber-400 hover:bg-slate-800">
            Apri la tua demo
          </Button>
        </a>
        <Button
          size="sm"
          variant="outline"
          className="h-8 border-slate-950/40 bg-transparent font-bold text-slate-950 hover:bg-slate-950/10"
          onClick={exitTenant}
          data-testid="esci-demo-nastro"
        >
          <DoorOpen className="mr-1 h-3.5 w-3.5" /> Esci dalla demo
        </Button>
      </div>
    </div>
  );
};

/** Demo con dati veri e modificabili: il tasto per tornare indietro sta sempre in alto. */
export const DemoNastro = () => {
  const { exitTenant } = useApp();
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 bg-sky-600 px-4 py-2 text-white" data-testid="demo-nastro">
      <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide sm:text-sm">
        <Sparkles className="h-4 w-4" /> Sala dimostrativa
        <span className="hidden font-medium normal-case tracking-normal opacity-90 sm:inline">
          — prova tutto quello che vuoi: sono dati finti
        </span>
      </p>
      <Button
        size="sm"
        className="h-8 bg-white font-bold text-sky-700 hover:bg-slate-100"
        onClick={exitTenant}
        data-testid="esci-demo-nastro"
      >
        <DoorOpen className="mr-1 h-3.5 w-3.5" /> Esci dalla demo
      </Button>
    </div>
  );
};
